import DotsLoading from 'components/loadings/dotsLoading'
import { SectionError } from 'components/states'
import posthog from 'posthog-js'
import { useCallback, useEffect, useRef, useState } from 'react'
import { FiChevronLeft } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { getMessagingPeople } from 'services/messagingHistory'
import { ChannelChips, ChannelCountChips } from './ChannelChips'
import { lastLine, recentLine } from './format'

const SORTS: { key: TMessagingPeopleSort; label: string }[] = [
    { key: 'recent', label: 'آخرین پیام' },
    { key: 'name', label: 'نام' },
]

const statusOf = (error: unknown) =>
    (error as { response?: { status?: number } })?.response?.status ?? null

function PersonCard({
    person,
    now,
    onOpen,
}: {
    person: IMessagingPerson
    now: number
    onOpen: () => void
}) {
    const body = (
        <>
            <div className=" flex items-center justify-between gap-2">
                <p className=" truncate text-sm font-bold text-heading">
                    {person.name}
                    {person.is_manager && (
                        <span className=" mr-1.5 rounded-full bg-brand-soft px-2 py-0.5 text-[10px] font-normal text-brand-deep">
                            مدیر
                        </span>
                    )}
                </p>
                {person.last && (
                    <FiChevronLeft className=" h-4 w-4 shrink-0 text-text-muted" />
                )}
            </div>

            {person.last ? (
                <>
                    <p className=" mt-1 text-xs text-text-body">
                        {lastLine(person.last, now)}
                    </p>
                    <ChannelChips channels={person.last.channels} />
                </>
            ) : (
                <p className=" mt-1 text-xs text-text-muted">
                    هنوز پیامی ارسال نشده
                </p>
            )}

            <p className=" mt-2 text-[11px] text-text-secondary">
                {recentLine(person.recent_messages, person.recent_days)}
            </p>
            <ChannelCountChips counts={person.recent_by_channel} />
        </>
    )

    const className =
        ' mb-3 block w-full rounded-2xl border border-[#EFEFEF] bg-white p-4 text-right shadow-card'

    // A member with nothing to show opens an empty timeline, so it is not a
    // link — a card that answers a tap with a blank page is the shape people
    // tap twice.
    return person.last ? (
        <button
            type="button"
            onClick={onOpen}
            className={`${className} active:scale-[0.99]`}
        >
            {body}
        </button>
    ) : (
        <div className={className}>{body}</div>
    )
}

/**
 * «اعضا» — the log read by person.
 *
 * The question this view answers is not "what did we send" but "what has this
 * member been told, and did any of it land": three reminders about the same
 * installment, all delivered, none opened, is a different situation from a
 * member we never reached.
 *
 * The first page arrives from the parent, which already fetched it to decide
 * whether the new backend is there at all; refetching it here would be a
 * second identical request on every open.
 */
export default function PeopleTab({
    fundId,
    initial,
}: {
    fundId: string
    initial: IMessagingPeopleResponse
}) {
    const navigate = useNavigate()
    const [sort, setSort] = useState<TMessagingPeopleSort>('recent')
    const [query, setQuery] = useState('')
    const [debouncedQuery, setDebouncedQuery] = useState('')
    const [items, setItems] = useState<IMessagingPerson[]>(
        initial.results ?? []
    )
    const [page, setPage] = useState(1)
    const [hasNext, setHasNext] = useState(!!initial.has_next)
    const [loading, setLoading] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [failed, setFailed] = useState(false)

    /**
     * One «now» per visit.
     *
     * «۲ ساعت پیش» recomputed on every render would flip a card's label in the
     * middle of a scroll for no reason a reader could name.
     */
    const now = useRef(Date.now()).current
    const loadedKey = useRef('recent|')

    /**
     * Only the newest request may write.
     *
     * Sort, search and page move on their own clocks, and the axios instance
     * cancels nothing: tap «موارد بیشتر» on a slow line and then type a name,
     * and the page-2 answer — unfiltered, and asked for first — arrives after
     * the filtered page 1 and appends itself to it.
     */
    const requestId = useRef(0)

    // the search hits a scan on the backend; per keystroke it would be one
    // request per letter of a member's name
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(query), 350)
        return () => clearTimeout(timer)
    }, [query])

    // Length only. The text of this field is a member's name.
    useEffect(() => {
        if (!debouncedQuery) return
        posthog.capture('announce_people_searched', {
            moneypool_id: Number(fundId),
            query_length: debouncedQuery.length,
        })
    }, [debouncedQuery, fundId])

    const loadPage = useCallback(
        async (pageNumber: number, reset: boolean) => {
            const token = ++requestId.current
            reset ? setLoading(true) : setLoadingMore(true)
            if (reset) setFailed(false)
            try {
                const data = await getMessagingPeople(fundId, {
                    sort,
                    q: debouncedQuery || undefined,
                    page: pageNumber,
                })
                if (token !== requestId.current) return
                const batch = data?.results ?? []
                setItems((prev) => (reset ? batch : [...prev, ...batch]))
                setHasNext(!!data?.has_next)
            } catch (error) {
                // reported even when superseded — the request did fail
                posthog.capture('announce_history_load_failed', {
                    screen: 'people',
                    moneypool_id: Number(fundId),
                    page: pageNumber,
                    status: statusOf(error),
                })
                if (token !== requestId.current) return
                if (reset) setItems([])
                setHasNext(false)
                // An outage has to look different from an empty fund: this
                // page's ancestor once rendered both as «چیزی نیست».
                setFailed(true)
            } finally {
                // a superseded answer must not take the spinner down from
                // under the request that replaced it
                if (token === requestId.current) {
                    setLoading(false)
                    setLoadingMore(false)
                }
            }
        },
        [fundId, sort, debouncedQuery]
    )

    // The parent's page-one answer is already on screen under the default
    // sort and no search; only a real change reloads.
    useEffect(() => {
        const key = `${sort}|${debouncedQuery}`
        if (key === loadedKey.current) return
        loadedKey.current = key
        setPage(1)
        loadPage(1, true)
    }, [sort, debouncedQuery, loadPage])

    useEffect(() => {
        posthog.capture('announce_people_viewed', {
            moneypool_id: Number(fundId),
            people_count: initial.results?.length ?? 0,
            members_total: initial.count,
            has_next: !!initial.has_next,
        })
    }, [fundId, initial])

    const onLoadMore = () => {
        const next = page + 1
        setPage(next)
        loadPage(next, false)
    }

    return (
        <>
            <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="جستجوی عضو"
                aria-label="جستجوی عضو"
                className=" mb-2.5 w-full rounded-[10px] border border-[#EFEFEF] bg-white px-3 py-3 text-xs text-text-body outline-none placeholder:text-text-muted"
            />

            <div
                role="tablist"
                aria-label="ترتیب فهرست"
                className=" mb-3 flex rounded-full border border-stroke bg-white p-1"
            >
                {SORTS.map((option) => {
                    const active = option.key === sort
                    return (
                        <button
                            key={option.key}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => {
                                if (active) return
                                posthog.capture(
                                    'announce_people_sort_changed',
                                    {
                                        moneypool_id: Number(fundId),
                                        sort: option.key,
                                    }
                                )
                                setSort(option.key)
                            }}
                            className={` flex-1 rounded-full py-2 text-[12px] transition-colors ${
                                active
                                    ? ' bg-green-1 font-bold text-white'
                                    : ' text-text-secondary'
                            }`}
                        >
                            {option.label}
                        </button>
                    )
                })}
            </div>

            {loading ? (
                <DotsLoading />
            ) : failed ? (
                <SectionError
                    message="فهرست اعضا دریافت نشد"
                    onRetry={() => loadPage(1, true)}
                />
            ) : items.length === 0 ? (
                <div className=" rounded-2xl border border-[#EFEFEF] bg-white px-4 py-10 text-center text-sm text-text-secondary">
                    {debouncedQuery
                        ? 'عضوی با این نام پیدا نشد'
                        : 'هنوز پیامی برای اعضای این صندوق ثبت نشده است'}
                </div>
            ) : (
                <>
                    {items.map((person) => (
                        <PersonCard
                            key={person.poolship_id ?? person.member_id}
                            person={person}
                            now={now}
                            onOpen={() =>
                                navigate(
                                    `/fund/announce/${fundId}/member/${person.member_id}`,
                                    { state: { name: person.name } }
                                )
                            }
                        />
                    ))}
                    {hasNext && (
                        <button
                            type="button"
                            onClick={onLoadMore}
                            disabled={loadingMore}
                            className=" my-3 w-full rounded-xl border border-green-1 py-2.5 text-sm text-green-1 disabled:opacity-60"
                        >
                            {loadingMore ? 'در حال بارگذاری...' : 'موارد بیشتر'}
                        </button>
                    )}
                </>
            )}
        </>
    )
}
